import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "./api";
import { turnstileToken } from "./turnstile";

/**
 * Signing in: an email address and a six-digit code, no password. Stories need no account; signing in keeps them on
 * every device (and, later, is how Patron knows you). On a device that already had stories, they move into the account.
 */

const ERRORS: Record<string, string> = {
  bad_email: "That doesn't look like an email address. Check it and try again.",
  too_many_codes: "Five codes have gone to that address this hour. Use the latest one, or try again in an hour.",
  email_unavailable: "Sign-in by email isn't switched on here yet.",
  email_failed: "The code couldn't be sent. Check the address, or try again in a minute.",
  wrong_code: "That code isn't right. Check the latest email and try again.",
  code_expired: "That code has expired or been used. Send a new one.",
  turnstile: "Couldn't confirm you're a person. Reload the page and try again.",
};
const explain = (e: unknown) => (e instanceof ApiError && ERRORS[e.code]) || "Something went wrong. Check your connection and try again.";

/** Sent on window after a sign-in, so other panels on the page (Patron, morning letters) catch up without a reload. */
export const SIGNED_IN = "weftwake:signedin";

export function AccountPanel({ onDone }: { onDone?(): void }) {
  const [account, setAccount] = useState<string | null | undefined>(undefined);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  useEffect(() => { api.account().then((r) => setAccount(r.email), () => setAccount(null)); }, []);
  useEffect(() => { if (step === "code") codeRef.current?.focus(); }, [step]);

  const send = async () => {
    setBusy(true); setError(null);
    try {
      const key = (await api.config().catch(() => null))?.turnstileSiteKey ?? null;
      await api.sendCode(email, await turnstileToken(key, "signin"));
      setStep("code"); setCode("");
    } catch (e) { setError(explain(e)); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.verify(email, code);
      // Another device's account (or stories moved into it): reload so the page shows the account's stories.
      if (r.switched || r.moved) { location.reload(); return; }
      setAccount(r.email); setStep("email"); onDone?.();
      window.dispatchEvent(new Event(SIGNED_IN));
    } catch (e) { setError(explain(e)); } finally { setBusy(false); }
  };
  const signOut = async () => {
    setBusy(true);
    try { await api.signOut(); location.reload(); } catch (e) { setError(explain(e)); setBusy(false); }
  };

  if (account === undefined) return <p className="lm-gate-small">…</p>;
  if (account) {
    return (
      <div className="lm-account">
        <p>Signed in as <strong>{account}</strong>. Your stories open on any device where you sign in with this address.</p>
        <div className="lm-privacy-actions">
          <button type="button" onClick={signOut} disabled={busy}>Sign out on this device</button>
        </div>
        {error && <p role="alert">{error}</p>}
      </div>
    );
  }
  return (
    <div className="lm-account">
      <p>You don't need an account to play. Sign in to keep your stories on every device: we email you a six-digit code, no password. If this device already has stories, they come with you.</p>
      {step === "email" ? (
        <form className="lm-account-form" onSubmit={(e) => { e.preventDefault(); if (email.trim()) void send(); }}>
          <label htmlFor="lm-account-email">Email address</label>
          <input id="lm-account-email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <button type="submit" disabled={busy || !email.trim()}>{busy ? "Sending…" : "Send me a code"}</button>
        </form>
      ) : (
        <form className="lm-account-form" onSubmit={(e) => { e.preventDefault(); if (code.trim().length === 6) void verify(); }}>
          <label htmlFor="lm-account-code">The six-digit code sent to {email.trim()}</label>
          <input id="lm-account-code" ref={codeRef} inputMode="numeric" autoComplete="one-time-code" pattern="\d{6}" maxLength={6}
            value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required />
          <div className="lm-privacy-actions">
            <button type="submit" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Sign in"}</button>
            <button type="button" onClick={() => { setStep("email"); setError(null); }} disabled={busy}>Use another address</button>
            <button type="button" onClick={send} disabled={busy}>Send a new code</button>
          </div>
          <p className="lm-gate-small">The code works for 10 minutes. Not there? Check your spam folder.</p>
        </form>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}
